// Экран сетлиста — DOM (единый сетлист, фаза 1, задача 9). Строки — те же
// органы, что во вкладке «Песни» библиотеки (library-view.ts/buildSongEl +
// ui/song-row-controls.ts): карточка = слова, справа «Слова» и «Звук», пустой
// слот называет причину. Один вид строки в двух местах — одно правило для
// глаза (решения дизайн-прохода 23.09,
// docs/superpowers/plans/2026-09-23-unified-setlist-phase-1.md).
//
// Модуль чистый по данным: сеть, роутинг и загрузка (какой сетлист, какая
// команда, тикающий лайв-якорь) остаются у задачи 10 (route.ts/main.ts) — сюда
// приходит уже готовая модель (setlist-screen/model.ts) и функция навигации.
import { t } from '../ui/i18n.js';
import { icon } from '../ui/icons.js';
import { buildSongRowControls } from '../ui/song-row-controls.js';
import { buildEmptyState } from '../ui/empty-state.js';
import { saveWordsOrigin } from '../song-words-view.js';
import type { ScreenRow } from './model.js';

export interface SetlistScreenLive { songNumber: number; path: string }

export interface SetlistScreenOpts {
  title: string;
  subtitle: string;
  rows: ScreenRow[];
  /** Номер песни (1-based), на которой лидер СЕЙЧАС, и куда ведёт лайв-пилюля. null — лидер не ведёт. */
  live: SetlistScreenLive | null;
  /** Куда вернёт крестик страницы слов — адрес этого экрана (сохраняется в sessionStorage). */
  originPath: string;
  navigate: (path: string) => void;
  /**
   * «‹ Library» рисуется, только когда у читателя ЕСТЬ библиотека, куда вести.
   * У гостя на публичном `/setlist/<id>` (библиотека ответила 401 — фикс
   * задачи 10) библиотеки нет: кнопка вела бы на экран входа, хотя сам экран
   * сетлиста гостю доступен без логина. Ссылка слов всегда `true` — до неё
   * не добраться, не имея команды.
   */
  showBack: boolean;
}

/** Ключ sessionStorage, которым уже пользуется страница слов (song-words-view.ts). */
const WORDS_NEIGHBORS_KEY = 'bw.words.neighbors';

/** Соседи страницы слов — снимок ИМЕННО этого рендера, в порядке экрана.
 *  Строки без слов исключены (та же причина, что у neighborsFromRows в
 *  song-words-view.ts): открыть их со страницы слов нечем. */
function saveNeighbors(rows: readonly ScreenRow[]): void {
  const list = rows.filter((r) => r.wordsId !== null).map((r) => ({ id: r.wordsId as string, name: r.name }));
  try { sessionStorage.setItem(WORDS_NEIGHBORS_KEY, JSON.stringify(list)); } catch { /* приватный режим */ }
}

/** Одна строка песни — ровно та же разметка, что buildSongEl рисует во
 *  вкладке «Песни» (lib-song › lib-card-row › song-body + row-controls), но
 *  из готовой ScreenRow, а не из SongRow библиотеки. */
function buildRow(row: ScreenRow, isNow: boolean, allRows: readonly ScreenRow[], opts: SetlistScreenOpts): HTMLElement {
  const songEl = document.createElement('div');
  songEl.className = 'lib-song';
  songEl.dataset.songId = row.key;

  const cardRow = document.createElement('div');
  cardRow.dataset.role = 'song-row';
  cardRow.className = 'lib-card-row' + (isNow ? ' is-now' : '');

  const hasWords = row.wordsId !== null;
  const hasAudio = row.audioPath !== null;

  // Открытие слов — ОДНО замыкание на оба входа строки (тело карточки и слот
  // «Слова» buildSongRowControls): до правки соседи/origin/navigate писались
  // дважды, и второе место рисковало разойтись с первым при следующей правке.
  const openWords = (): void => {
    saveNeighbors(allRows);
    saveWordsOrigin({ path: opts.originPath });
    opts.navigate('/words/' + encodeURIComponent(row.wordsId as string));
  };

  const body = document.createElement('button');
  body.type = 'button';
  body.dataset.role = 'song-body';
  body.className = 'song-card song-body';

  const main = document.createElement('span');
  main.className = 'song-main';

  const nameEl = document.createElement('span');
  nameEl.className = 'song-name';
  nameEl.textContent = row.name || t('library.untitled');
  main.appendChild(nameEl);

  // Мета: «Now» первым токеном у текущей песни (зелёное, задача дизайна),
  // затем прежняя мета через « · », если она есть; у строки без своей меты —
  // одно «Now», без висящего разделителя. Ни «Now», ни меты — слота нет вовсе
  // (та же экономия, что у library-view.ts songMetaTokens).
  if (isNow || row.meta) {
    const metaEl = document.createElement('span');
    metaEl.dataset.role = 'song-meta';
    metaEl.className = 'song-sub';
    if (isNow) {
      const nowSpan = document.createElement('span');
      nowSpan.className = 'ss-now';
      nowSpan.textContent = t('setlistScreen.now');
      metaEl.appendChild(nowSpan);
      if (row.meta) metaEl.appendChild(document.createTextNode(' · ' + row.meta));
    } else {
      metaEl.textContent = row.meta;
    }
    main.appendChild(metaEl);
  }
  body.appendChild(main);

  if (hasWords) {
    body.addEventListener('click', openWords);
  } else {
    body.setAttribute('aria-disabled', 'true');
    body.classList.add('is-disabled');
    body.title = t('library.wordsNotPublished');
  }
  cardRow.appendChild(body);

  const controls = buildSongRowControls({
    hasWords,
    hasAudio,
    labels: {
      words: t('library.wordsEntry'),
      audio: t('library.audioEntry'),
      audioVersions: t('library.audioVersions'),
      noWords: t('library.noWordsSlot'),
      noAudio: t('library.noAudioSlot'),
    },
    onWords: openWords,
    onAudio: () => {
      if (row.audioPath) opts.navigate(row.audioPath);
    },
  });
  cardRow.appendChild(controls);

  songEl.appendChild(cardRow);
  return songEl;
}

/** Монтирует экран сетлиста в `host`. Чистая функция от готовых данных —
 *  повторный вызов перерисовывает всё заново (`unmount` просто чистит host;
 *  подписок или таймеров модуль не заводит). */
export function mountSetlistScreen(host: HTMLElement, opts: SetlistScreenOpts): { unmount(): void } {
  host.innerHTML = '';

  const root = document.createElement('section');
  root.dataset.role = 'setlist-screen';
  root.className = 'ss-root';

  // ‹ Library — ghost-кнопка, ведёт в библиотеку (решение дизайн-прохода:
  // числа песен и вкладки в шапке экрана нет — это отдельный экран, не
  // вкладка библиотеки). Гостю (opts.showBack === false) вести некуда — у
  // него самой библиотеки нет (фикс задачи 10: 401 у /api/library на
  // публичном /setlist/<id>).
  if (opts.showBack) {
    const back = document.createElement('button');
    back.type = 'button';
    back.dataset.role = 'setlist-back';
    back.className = 'ss-back';
    back.innerHTML = icon('chevronLeft');
    const backLabel = document.createElement('span');
    backLabel.textContent = t('setlistScreen.back');
    back.appendChild(backLabel);
    back.addEventListener('click', () => opts.navigate('/library'));
    root.appendChild(back);
  }

  const head = document.createElement('header');
  head.className = 'ss-head';
  const h1 = document.createElement('h1');
  h1.className = 'ss-title';
  h1.textContent = opts.title || t('library.untitled');
  head.appendChild(h1);
  if (opts.subtitle) {
    const sub = document.createElement('p');
    sub.className = 'ss-sub';
    sub.textContent = opts.subtitle;
    head.appendChild(sub);
  }
  root.appendChild(head);

  // Лайв-пилюля — ТОЛЬКО когда лидер сейчас ведёт (свежий якорь); тап ведёт в
  // чтение за лидером (текущий переход песни), а не просто открывает песню.
  if (opts.live) {
    const live = opts.live;
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.dataset.role = 'setlist-live';
    pill.className = 'ss-live';
    pill.textContent = t('setlistScreen.live', { n: live.songNumber });
    pill.addEventListener('click', () => opts.navigate(live.path));
    root.appendChild(pill);
  }

  if (opts.rows.length === 0) {
    root.appendChild(buildEmptyState({
      icon: 'musicOff',
      title: t('setlistScreen.empty'),
      dataRole: 'setlist-empty',
    }));
  } else {
    // Обёртка `.lib-panel > section > .lib-plaque` — минимум, за который
    // цепляются правила `library.css` (см. renderSongsPanel в
    // ui/library-view.ts: та же вложенность оборачивает `[data-role="songs-list"]`
    // там). Без неё срабатывают только НЕскоуп-правила (габарит слотов,
    // отступы карточки) — материальная подложка плашки/строк, добавленная
    // парными `body[data-view='setlist']`-правилами в library.css, до
    // строк без этой обёртки не доезжает.
    const panel = document.createElement('div');
    panel.className = 'lib-panel';
    const section = document.createElement('section');
    const list = document.createElement('div');
    list.dataset.role = 'songs-list';
    list.className = 'lib-plaque';

    const nowIdx = opts.live && opts.live.songNumber >= 1 && opts.live.songNumber <= opts.rows.length
      ? opts.live.songNumber - 1
      : -1;
    opts.rows.forEach((row, i) => list.appendChild(buildRow(row, i === nowIdx, opts.rows, opts)));

    section.appendChild(list);
    panel.appendChild(section);
    root.appendChild(panel);
  }

  host.appendChild(root);

  return { unmount(): void { host.innerHTML = ''; } };
}
